import axios from 'axios';

const DB_NAME = 'thrive-offline';
const QUEUE = 'queue';
const CACHE = 'cache';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const QUEUE_EVENT = 'offline-queue-changed';

// GET endpoints whose responses are kept on the phone for offline field work
const CACHEABLE = [/\/api\/hives(\/[^/?]+)?(\?|$)/, /\/api\/yards/, /\/api\/beekeepers/, /\/api\/inspections(\?|$)/, /\/api\/inspections\/summaries/,
  /\/api\/task-types/, /\/api\/action-types/, /\/api\/subscription\/upload-limits/, /\/api\/user\/weight-settings/, /\/api\/weight-settings/,
  /\/api\/harvest-config/, /\/api\/harvests(\/summary)?(\?|$)/, /\/api\/pending-harvests/, /\/api\/queens/, /\/api\/hive-types/, /\/api\/auth\/me/,
  /\/api\/features/, /\/api\/settings\/public/, /\/api\/app-settings/, /\/api\/weights/,
  /\/api\/hives\/[^/?]+\/(weight-history|baseline-weight|timeline|open-tasks|stack-history)/, /\/api\/hive-splits(\?|$)/, /\/api\/mite-counts/, /\/api\/treatment-plans/,
  /\/api\/treatment-types/, /\/api\/queen-rearing\/batches(\?|$)/, /\/api\/planned-actions(\?|$)/, /\/api\/crew\/tasks(\?|$)/];

let dbPromise;
const db = () => {
  if (!dbPromise) {
    dbPromise = new Promise((resolve, reject) => {
      const req = indexedDB.open(DB_NAME, 1);
      req.onupgradeneeded = () => {
        req.result.createObjectStore(QUEUE, { keyPath: 'id' });
        req.result.createObjectStore(CACHE);
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => reject(req.error);
    });
  }
  return dbPromise;
};

const tx = async (store, mode, fn) => {
  const d = await db();
  return new Promise((resolve, reject) => {
    const t = d.transaction(store, mode);
    const r = fn(t.objectStore(store));
    t.oncomplete = () => resolve(r?.result);
    t.onerror = () => reject(t.error);
  });
};

export const isOfflineError = (err) => !err?.response || (err.response.status === 503 && err.response.data?.error === 'offline');

export const listQueue = () => tx(QUEUE, 'readonly', s => s.getAll()).then(r => (r || []).sort((a, b) => a.created_at.localeCompare(b.created_at)));

const notify = async () => {
  const items = await listQueue().catch(() => []);
  window.dispatchEvent(new CustomEvent(QUEUE_EVENT, { detail: items }));
};

// kind: 'inspection' | 'weights' | 'harvest'; photos: [{ blob, name, section, field }]
// followUps: [{ method, path, body }] run in order after the main request succeeds
export const enqueue = async ({ kind, method = 'post', path, body, photos = [], label, hiveIds = [], followUps = [] }) => {
  const item = { id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`, kind, method, path, body, photos, label, hiveIds, followUps, created_at: new Date().toISOString() };
  await tx(QUEUE, 'readwrite', s => s.put(item));
  notify();
  return item;
};

const uploadPhoto = async (photo, headers) => {
  const fd = new FormData();
  fd.append('file', photo.blob, photo.name || 'photo.jpg');
  const res = await axios.post(`${API}/inspections/upload-photo`, fd, { headers: { ...headers, 'Content-Type': 'multipart/form-data' } });
  return res.data.path;
};

let syncing = false;
export const syncQueue = async ({ includeFailed = false } = {}) => {
  const token = localStorage.getItem('crewToken') || localStorage.getItem('token');
  if (syncing || !token || !navigator.onLine) return { synced: 0, failed: 0 };
  syncing = true;
  const headers = { Authorization: `Bearer ${token}` };
  let synced = 0, failed = 0;
  try {
    for (const item of await listQueue()) {
      if (item.error && !includeFailed) continue;
      let current = item;
      try {
        if (!item.mainDone) {
          const body = { ...item.body };
          for (const p of item.photos || []) {
            const path = await uploadPhoto(p, headers);
            const field = p.field || 'photos';
            body[field] = [...(body[field] || []), path];
          }
          await axios({ method: item.method, url: `${API}${item.path}`, data: body, headers });
          current = { ...item, mainDone: true, photos: [], error: undefined };
          await tx(QUEUE, 'readwrite', s => s.put(current));
        }
        const remaining = [...(current.followUps || [])];
        while (remaining.length) {
          const f = remaining[0];
          try { await axios({ method: f.method, url: `${API}${f.path}`, data: f.body, headers }); }
          catch (fe) { if (isOfflineError(fe)) throw fe; }
          remaining.shift();
          current = { ...current, followUps: [...remaining] };
          await tx(QUEUE, 'readwrite', s => s.put(current));
        }
        await tx(QUEUE, 'readwrite', s => s.delete(item.id));
        synced += 1;
      } catch (err) {
        if (isOfflineError(err)) break;
        failed += 1;
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : Array.isArray(detail) ? detail.map(d => `${(d.loc || []).slice(-1)[0] || 'field'}: ${d.msg || 'invalid'}`).join('; ') : `Sync failed (${err.response?.status || 'error'})`;
        await tx(QUEUE, 'readwrite', s => s.put({ ...current, error: msg }));
      }
    }
  } finally {
    syncing = false;
    if (synced) {
      window.dispatchEvent(new Event('refreshData'));
      if (!includeFailed) import('sonner').then(({ toast }) => toast.success(`Back online: ${synced} offline record${synced > 1 ? 's' : ''} synced`));
    }
    notify();
  }
  return { synced, failed };
};

// Send a field record now, or keep it on the phone when there's no signal. Returns { queued } or throws real errors.
export const sendOrQueue = async ({ method = 'post', path, body, kind, label, hiveIds = [], followUps = [] }) => {
  const token = localStorage.getItem('crewToken') || localStorage.getItem('token');
  const headers = token ? { Authorization: `Bearer ${token}` } : {};
  let res;
  try {
    res = await axios({ method, url: `${API}${path}`, data: body, headers });
  } catch (err) {
    if (!isOfflineError(err)) throw err;
    await enqueue({ kind, method, path, body, label, hiveIds, followUps });
    return { queued: true };
  }
  for (let i = 0; i < followUps.length; i++) {
    const f = followUps[i];
    try { await axios({ method: f.method, url: `${API}${f.path}`, data: f.body, headers }); }
    catch (err) {
      if (!isOfflineError(err)) throw err;
      const item = await enqueue({ kind, method, path, body, label, hiveIds, followUps: followUps.slice(i) });
      await tx(QUEUE, 'readwrite', s => s.put({ ...item, mainDone: true }));
      return { queued: true, data: res.data };
    }
  }
  return { queued: false, data: res.data };
};

export const queuedToast = (what) => import('sonner').then(({ toast }) => toast.success(`No signal: ${what} saved on this phone`, { description: 'It will upload automatically when you are back in coverage.', duration: 6000 }));

export const discardQueued = async (id) => { await tx(QUEUE, 'readwrite', s => s.delete(id)); notify(); };

// Axios layer: remember field data GETs, serve them when there's no signal
let installed = false;
export const installOfflineCache = () => {
  if (installed || typeof indexedDB === 'undefined') return;
  installed = true;
  const keyOf = (config) => {
    const params = config.params ? `?${new URLSearchParams(config.params).toString()}` : '';
    return `${config.url}${params}`.replace(/([?&])_t=\d+&?/, '$1').replace(/[?&]$/, '');
  };
  const cacheable = (config) => (config.method || 'get').toLowerCase() === 'get' && CACHEABLE.some(re => re.test(config.url || ''));
  axios.interceptors.response.use(
    (res) => {
      if (res.headers?.['x-offline-cache'] === 'true') { res.offline = true; return res; }
      if (cacheable(res.config)) tx(CACHE, 'readwrite', s => s.put(res.data, keyOf(res.config))).catch(() => {});
      return res;
    },
    async (err) => {
      const config = err.config;
      if (config && cacheable(config) && isOfflineError(err)) {
        const data = await tx(CACHE, 'readonly', s => s.get(keyOf(config))).catch(() => undefined);
        if (data !== undefined) return { data, status: 200, statusText: 'OK (offline cache)', headers: {}, config, offline: true };
      }
      return Promise.reject(err);
    }
  );
  window.addEventListener('online', () => syncQueue());
  setTimeout(() => refreshSavedApiaries({ appOpen: true }), 20000);
  setTimeout(() => {
    const token = localStorage.getItem('token');
    if (!token || !navigator.onLine) return;
    ['/treatment-types', '/task-types', '/action-types', '/weight-settings'].forEach(p => axios.get(`${API}${p}`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {}));
  }, 8000);
  setInterval(() => refreshSavedApiaries(), 15 * 60 * 1000);
  document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'visible') refreshSavedApiaries({ appOpen: true }); });
  navigator.connection?.addEventListener?.('change', () => refreshSavedApiaries());
  setInterval(() => { if (navigator.onLine) syncQueue(); }, 60000);
  setTimeout(() => syncQueue(), 3000);
};

// Download every hive page in an apiary so it opens with no signal
const SAVED_KEY = 'offline-saved-apiaries';
export const getSavedApiaries = () => { try { return JSON.parse(localStorage.getItem(SAVED_KEY) || '{}'); } catch { return {}; } };

export const saveApiaryOffline = async (yardName, hives, onProgress) => {
  const token = localStorage.getItem('crewToken') || localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const get = (path) => axios.get(`${API}${path}`, { headers }).catch(() => null);
  await Promise.all(['/yards', '/hives', '/weight-settings', '/hives/latest-inspections', '/beekeepers', '/inspections', '/task-types',
    '/action-types', '/subscription/upload-limits', '/treatment-types', '/queen-rearing/batches'].map(get));
  const yardIds = [...new Set(hives.map(h => h.yard_id).filter(Boolean))];
  await Promise.all([...yardIds, yardName].map(y => get(`/hives?yard_id=${y}`)));
  let done = 0, failed = 0;
  for (const h of hives) {
    const results = await Promise.all([`/hives/${h.id}`, `/inspections?hive_id=${h.id}`, `/hives/${h.id}/weight-history?limit=10`,
      `/mite-counts?hive_id=${h.id}`, `/treatment-plans?hive_id=${h.id}`, `/treatment-plans?hive_id=${h.id}&status=active`,
      `/hives/${h.id}/timeline`, `/hives/${h.id}/open-tasks`, `/hives/${h.id}/baseline-weight`, `/hives/${h.id}/stack-history`, `/hive-splits?hive_id=${h.id}`].map(get));
    if (!results[0]) failed += 1;
    done += 1;
    onProgress?.(done, hives.length);
  }
  if (failed === hives.length && hives.length) throw new Error('offline');
  const saved = { ...getSavedApiaries(), [yardName]: { at: new Date().toISOString(), count: hives.length - failed } };
  localStorage.setItem(SAVED_KEY, JSON.stringify(saved));
  window.dispatchEvent(new Event(SAVED_EVENT));
  return saved[yardName];
};

// Background refresh of saved apiaries: on Wi-Fi only, at most every 3 hours.
// Phones that don't report Wi-Fi (iPhone) refresh only when the app is opened with signal.
export const SAVED_EVENT = 'offline-apiaries-updated';
const REFRESH_MS = 3 * 60 * 60 * 1000;
const onWifi = () => {
  const c = navigator.connection;
  if (!c || !c.type) return null;
  return ['wifi', 'ethernet'].includes(c.type);
};
let refreshing = false;
export const refreshSavedApiaries = async ({ appOpen = false } = {}) => {
  const wifi = onWifi();
  if (refreshing || !navigator.onLine || wifi === false || (wifi === null && !appOpen)) return 0;
  const stale = Object.entries(getSavedApiaries()).filter(([, v]) => Date.now() - new Date(v.at).getTime() > REFRESH_MS).map(([name]) => name);
  const token = localStorage.getItem('crewToken') || localStorage.getItem('token');
  if (!stale.length || !token) return 0;
  refreshing = true;
  try {
    const res = await axios.get(`${API}/hives`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.offline) return 0;
    for (const name of stale) await saveApiaryOffline(name, (res.data || []).filter(h => h.yard === name)).catch(() => {});
    return stale.length;
  } catch { return 0; } finally { refreshing = false; }
};
